import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Calendar, MapPin, Users, Check, UserPlus, Loader2, ArrowLeft, Pencil, Tag } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import RichText, { isHtml } from "@/components/RichText";
import { EventRegisterDialog } from "@/components/EventRegisterDialog";

function status(dateStr) {
  if (!dateStr) return "archiwum";
  return new Date(dateStr) >= new Date() ? "nadchodzące" : "archiwum";
}

export default function SzkolenieDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tr, setTr] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [signed, setSigned] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [priceOpen, setPriceOpen] = useState(false);
  const [regTr, setRegTr] = useState(null);

  const load = () => api.get(`/trainings/${id}`).then((r) => setTr(r.data)).catch(() => setNotFound(true));
  const loadSigned = () => {
    if (user && user.approved !== false) api.get("/my-signups").then((r) => setSigned(r.data.includes(id))).catch(() => {});
    else setSigned(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => { loadSigned(); /* eslint-disable-next-line */ }, [user, id]);

  const toggleSignup = async () => {
    if (!user) { toast.info("Zaloguj się, aby zapisać się na wydarzenie"); navigate("/login", { state: { from: `/szkolenia/${id}` } }); return; }
    if (user.approved === false) { toast.info("Twoje konto oczekuje na zatwierdzenie przez administratora"); return; }
    setBusy(true);
    try {
      if (signed) { await api.delete(`/trainings/${id}/signup`); toast.success("Zrezygnowano z udziału"); }
      else { await api.post(`/trainings/${id}/signup`); toast.success("Zapisano na wydarzenie!"); }
      loadSigned();
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Błąd"); }
    finally { setBusy(false); }
  };

  if (notFound) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-32 text-center sm:px-6">
        <h1 className="font-display text-3xl font-bold text-ztk-navy dark:text-white">Nie znaleziono wydarzenia</h1>
        <Button asChild className="mt-6 bg-ztk-blue"><Link to="/szkolenia">Wróć do szkoleń</Link></Button>
      </div>
    );
  }

  if (!tr) {
    return <div className="flex min-h-[60vh] items-center justify-center pt-[68px]"><Loader2 className="h-8 w-8 animate-spin text-ztk-blue" /></div>;
  }

  const st = status(tr.event_date);
  const upcoming = st === "nadchodzące";
  const cap = tr.capacity || 0;
  const remaining = cap > 0 ? Math.max(0, cap - (tr.signup_count || 0)) : null;
  const full = cap > 0 && remaining <= 0 && !signed;
  const images = tr.images || [];
  const cover = images.length ? fileUrl(images[0]) : "https://images.unsplash.com/photo-1540575467063-178a50c2df87?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

  return (
    <div className="pt-[68px]" data-testid="szkolenie-detail">
      {/* HERO */}
      <section className="relative overflow-hidden bg-ztk-navydark text-white">
        <div className="absolute inset-0">
          <img src={cover} alt={tr.title} className="h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-t from-ztk-navydark via-ztk-navydark/80 to-ztk-navydark/40" />
        </div>
        <div className="relative mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <Button asChild variant="ghost" size="sm" className="mb-6 gap-1.5 text-slate-300 hover:text-white" data-testid="detail-back">
            <Link to="/szkolenia"><ArrowLeft className="h-4 w-4" /> Wszystkie szkolenia</Link>
          </Button>
          {user?.role === "admin" && (
            <Button asChild size="sm" className="mb-6 ml-2 gap-1.5 bg-ztk-yellow text-ztk-navydark hover:bg-ztk-yellow/90" data-testid="detail-admin-edit">
              <Link to="/_ztkig_admin?m=trainings"><Pencil className="h-4 w-4" /> Edytuj w panelu</Link>
            </Button>
          )}
          <span className={`label-mono inline-flex rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wide ${upcoming ? "bg-emerald-500 text-white" : "bg-slate-700 text-slate-200"}`}>{st}</span>
          {tr.kind === "konferencja" && <span className="label-mono ml-2 inline-flex rounded-full bg-ztk-yellow px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-ztk-navydark" data-testid="detail-kind">Konferencja</span>}
          <h1 className="mt-4 font-display text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl" data-testid="detail-title">{tr.title}</h1>
          <div className="mt-6 flex flex-wrap items-center gap-5 text-sm text-slate-300">
            <span className="flex items-center gap-2"><Calendar className="h-4 w-4 text-ztk-glow" />{tr.event_date ? new Date(tr.event_date).toLocaleDateString("pl-PL", { year: "numeric", month: "long", day: "numeric" }) : "Termin wkrótce"}{tr.end_date ? ` – ${new Date(tr.end_date).toLocaleDateString("pl-PL", { year: "numeric", month: "long", day: "numeric" })}` : ""}</span>
            {tr.location && <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-ztk-glow" />{tr.location}</span>}
            {cap > 0 && <span className="flex items-center gap-2"><Users className="h-4 w-4 text-ztk-glow" />{full ? "Brak wolnych miejsc" : `${remaining} wolnych miejsc`}</span>}
            {(() => {
              const pk = tr.packages || [];
              if (pk.length) {
                return <span className="flex items-center gap-2 font-semibold text-white" data-testid="detail-price"><Tag className="h-4 w-4 text-ztk-glow" />{pk.length > 1 ? `od ${pk[0].price}` : pk[0].price}</span>;
              }
              if (tr.price) {
                return tr.price_includes
                  ? <button onClick={() => setPriceOpen(true)} className="flex items-center gap-2 rounded-full bg-ztk-blue/25 px-3 py-1 font-semibold text-white transition-colors hover:bg-ztk-blue/50" data-testid="detail-price"><Tag className="h-4 w-4 text-ztk-glow" />{tr.price} <span className="text-xs font-normal opacity-80">— co zawiera?</span></button>
                  : <span className="flex items-center gap-2 font-semibold text-white" data-testid="detail-price"><Tag className="h-4 w-4 text-ztk-glow" />{tr.price}</span>;
              }
              return null;
            })()}
          </div>
          {upcoming || tr.kind === "konferencja" ? (
            <Button onClick={() => setRegTr(tr)} data-testid="detail-register" className="mt-8 gap-2 bg-ztk-blue hover:bg-ztk-glow">
              <UserPlus className="h-4 w-4" /> Weź udział
            </Button>
          ) : null}
        </div>
        <div className="cable-accent h-[3px] w-full" />
      </section>

      {/* BODY */}
      <section className="mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:px-8">
        <h2 className="font-display text-2xl font-bold text-ztk-navy dark:text-white">O wydarzeniu</h2>
        <div className="mt-4">
          {(() => {
            const content = tr.long_description || tr.description || "";
            return isHtml(content)
              ? <RichText html={content} className="prose-ztk text-muted-foreground" />
              : <p className="whitespace-pre-line leading-relaxed text-muted-foreground" data-testid="detail-description">{content || "Szczegóły wkrótce."}</p>;
          })()}
        </div>

        {tr.agenda && (
          <div className="mt-12" data-testid="detail-agenda">
            <h2 className="font-display text-2xl font-bold text-ztk-navy dark:text-white">Agenda</h2>
            <div className="mt-4">
              {isHtml(tr.agenda)
                ? <RichText html={tr.agenda} className="prose-ztk text-muted-foreground" />
                : <p className="whitespace-pre-line leading-relaxed text-muted-foreground">{tr.agenda}</p>}
            </div>
          </div>
        )}

        {(tr.packages || []).length > 0 && (
          <div className="mt-14" data-testid="detail-packages">
            <h2 className="font-display text-2xl font-bold text-ztk-navy dark:text-white">Pakiety i ceny</h2>
            <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {tr.packages.map((p, i) => (
                <div key={i} className="flex flex-col rounded-2xl border border-border bg-card p-6" data-testid={`package-card-${i}`}>
                  <h3 className="font-display text-lg font-semibold text-ztk-navy dark:text-white">{p.name || "Pakiet"}</h3>
                  <p className="mt-1 font-display text-2xl font-extrabold text-ztk-blue">{p.price}</p>
                  <ul className="mt-4 space-y-2">
                    {(p.includes || "").split("\n").map((l) => l.trim()).filter(Boolean).map((l, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-muted-foreground"><Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />{l}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* GALLERY */}
        {images.length > 0 && (
          <div className="mt-14">
            <h2 className="font-display text-2xl font-bold text-ztk-navy dark:text-white">Galeria zdjęć</h2>
            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="detail-gallery">
              {images.map((img, i) => (
                <button key={i} type="button" onClick={() => setLightbox(fileUrl(img))}
                  className="group overflow-hidden rounded-xl border border-border" data-testid={`detail-gallery-img-${i}`}>
                  <img src={fileUrl(img)} alt={`${tr.title} ${i + 1}`} className="h-56 w-full object-cover transition-transform group-hover:scale-105" />
                </button>
              ))}
            </div>
          </div>
        )}
      </section>

      {tr.price_includes && (
        <Dialog open={priceOpen} onOpenChange={setPriceOpen}>
          <DialogContent data-testid="detail-price-dialog">
            <DialogHeader><DialogTitle className="font-display">Cena {tr.price} — co zawiera</DialogTitle></DialogHeader>
            <ul className="space-y-2">
              {tr.price_includes.split("\n").map((line) => line.trim()).filter(Boolean).map((line, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />{line}
                </li>
              ))}
            </ul>
          </DialogContent>
        </Dialog>
      )}

      <EventRegisterDialog training={regTr} onClose={() => setRegTr(null)} onSuccess={load} />

      {lightbox && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-4" onClick={() => setLightbox(null)} data-testid="detail-lightbox">
          <img src={lightbox} alt="" className="max-h-[90vh] max-w-[90vw] rounded-lg object-contain" />
        </div>
      )}
    </div>
  );
}
